import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Images, Play, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import Slideshow from "./Slideshow";
import api from "../lib/api";
import { useProgress } from "../context/ProgressContext";

const token = () => localStorage.getItem("tycc_token");
export const photoUrl = (id, size) => `${process.env.REACT_APP_BACKEND_URL}/api/cheer-photos/${id}?size=${size}&auth=${token()}`;
const when = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

// Private family photo wall (owner + co-parents): every cheer photo grandparents have sent.
const PhotoWall = ({ limit = 8, showFilters = false, embedded = true }) => {
  const { children } = useProgress();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [show, setShow] = useState(false);
  const [booking, setBooking] = useState(false);

  const downloadBook = async () => {
    setBooking(true);
    try {
      const year = new Date().getFullYear();
      const r = await api.get(`/family/photo-book?year=${year}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `family-photo-book-${year}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Photo book ready", { description: `${r.headers["x-photo-count"] ?? ""} photo${r.headers["x-photo-count"] === "1" ? "" : "s"} plus each child's report card - print it double-sided.` });
    } catch (e) { toast.error("Could not build the photo book", { description: e?.response?.status === 404 ? "Add a child first." : "Please try again in a moment." }); } finally { setBooking(false); }
  };

  useEffect(() => {
    if (!children.length) return;
    api.get("/family/photos").then((r) => setData(r.data)).catch(() => setData({ photos: [], children: [] }));
  }, [children.length]);

  if (!data) return null;
  const all = data.photos.filter((p) => filter === "all" || p.child_id === filter);
  const shown = limit ? all.slice(0, limit) : all;
  if (embedded && data.photos.length === 0) return null;
  const current = data.photos.find((p) => p.id === open);

  return (
    <div className={embedded ? "bg-white rounded-3xl shadow-md p-6 sm:p-8" : ""} data-testid="photo-wall">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-[#fff0f5] text-[#e5356f] flex items-center justify-center flex-shrink-0"><Images /></span>
        <div className="flex-1">
          <h3 className="font-bold text-[#0c4a6e] text-xl" style={{ fontFamily: "Literata, serif" }}>Family photo wall</h3>
          <p className="text-gray-600 text-sm mt-1">Every photo grandparents and mentors have sent with a cheer - private to your family. <span className="font-semibold" data-testid="photo-wall-count">{all.length} photo{all.length === 1 ? "" : "s"}{filter !== "all" ? ` of ${data.photos.length}` : ""}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {all.length > 0 && <button type="button" onClick={() => setShow(true)} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white text-sm inline-flex items-center gap-1.5" data-testid="photo-wall-slideshow"><Play size={14} fill="#fff" /> Play slideshow</button>}
          <button type="button" onClick={downloadBook} disabled={booking} className="btn-pill bg-white border-2 border-[#7d5fb2] text-[#7d5fb2] hover:bg-[#f3e8ff] text-sm inline-flex items-center gap-1.5 disabled:opacity-60" title="Cover, each child's report card and this year's cheer photos" data-testid="photo-book-download"><BookOpen size={14} /> {booking ? "Making your book..." : `Photo book PDF (${new Date().getFullYear()})`}</button>
          {embedded && data.photos.length > limit && <Link to="/photos" className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white text-sm" data-testid="photo-wall-see-all">See all {data.photos.length}</Link>}
        </div>
      </div>
      {show && <Slideshow photos={all} onClose={() => setShow(false)} />}
      {showFilters && data.children.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="photo-wall-filters">
          {[{ id: "all", name: "Everyone" }, ...data.children].map((c) => (
            <button key={c.id} type="button" onClick={() => setFilter(c.id)} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${filter === c.id ? "bg-[#1b8bc9] text-white" : "bg-[#e9f9fd] text-[#1b8bc9] hover:bg-[#c9ecf5]"}`} data-testid={`photo-filter-${c.id}`}>{c.name}</button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500 inline-flex items-center gap-2" data-testid="photo-wall-empty"><Camera size={16} /> No photos yet. Grandparents can attach a photo to any cheer.</p>
      ) : (
        <div className={`mt-5 grid gap-3 ${embedded ? "grid-cols-4 sm:grid-cols-8" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`} data-testid="photo-wall-grid">
          {shown.map((p) => (
            <button key={p.id} type="button" onClick={() => setOpen(p.id)} className="group relative aspect-square rounded-2xl overflow-hidden bg-[#f7fdfe] shadow hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`photo-tile-${p.id}`}>
              <img src={photoUrl(p.id, "thumb")} alt={`${p.author_name} for ${p.child_name}`} className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent text-white text-[11px] font-bold px-2 pt-6 pb-1.5 text-left truncate">{p.child_name} · {p.author_name}</span>
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!current} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white rounded-3xl" data-testid="photo-wall-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {current && (
            <>
              <img src={photoUrl(current.id, "full")} alt={`${current.author_name} for ${current.child_name}`} className="w-full max-h-[75vh] object-contain rounded-2xl" />
              <div className="px-3 pb-2 text-sm text-[#0c4a6e]" data-testid="photo-wall-caption"><span className="font-bold">{current.author_name}</span> to <span className="font-bold">{current.child_name}</span> · {when(current.created_at)}<p className="text-gray-600 mt-0.5">“{current.text}”</p></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoWall;
